import { Component, OnInit, ViewChild, ElementRef, signal, computed, Input, EventEmitter, Output, OnChanges, effect, HostListener } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { IBaseOption } from '../../../models/base.model';
import { FlowObjectType, FlowObjectDefinition, FormSchema } from '../../../models/flow-object.model';
import { FlowDefinition, FlowTargetLabel } from '../../../models/flow.model';
import { FlowObjectDefinitionService } from '../../../services/flow-object-definition.service';
import { Enums } from '../../../shared/enums';
import { VisibleArea } from '../../../shared/helper-classes';
import { FORMBUILDER_SCHEMA_DEFAULT } from '../../formio/formbuilder-schema-default';
import { PdfPreviewDialogComponent } from '../../pdf-preview-dialog/pdf-preview-dialog.component';
import { FlowDefinitionDetailsDefaultComponent } from '../flow-definition/default/fdd-default.component';
import { FlowDefinitionDetailsHotConfigsComponent } from '../flow-definition/hot-configs/fdd-hot-configs.component';
import { FlowObjectDetailsDefaultComponent } from '../flow-object/default/fod-default.component';
import { FlowObjectDetailsExtraContentComponent } from '../flow-object/extra-content/fod-extra-content.component';
import { FOD_EXTRA_CONTENT_MESSAGE_TINYMCE_OPTIONS, FOD_EXTRA_CONTENT_WAITING_MESSAGE_TINYMCE_OPTIONS } from '../flow-object/extra-content/tinymce-options';
import { FlowObjectDetailsFormComponent } from '../flow-object/form/fod-form.component';
import { FlowObjectDetailsGatewayPathsComponent } from '../flow-object/gateway-paths/fod-gateway-paths.component';
import { FlowObjectDetailsGatewayRulesComponent } from '../flow-object/gateway-rules/fod-gateway-rules.component';
import { FlowObjectDetailsMidwayOutboundApiComponent } from '../flow-object/midway-outbound-api/fod-midway-outbound-api.component';
import { FlowObjectDetailsModelEditorComponent } from '../flow-object/model-editor/fod-model-editor.component';
import { FOD_MODEL_EDITOR_TINYMCE_OPTIONS } from '../flow-object/model-editor/tinymce-options';
import { HTML_TEMPLATE_DEFAULT } from '../flow-object/model-editor/tinymce-template/tinymce-template-default';
import { FlowObjectDetailsOutboundApiComponent } from '../flow-object/outbound-api/fod-outbound-api.component';
import { FlowObjectDetailsStartInboundComponent } from '../flow-object/start-inbound-api/fod-start-inbound-api.component';
import { IMPORTS } from './_imports';
import { Utils } from '../../../shared/utils';

@Component({
    selector: 'details-manager',
    templateUrl: './details-manager.component.html',
    styleUrl: './details-manager.component.scss',
    standalone: true,
    imports: IMPORTS
})
export class DetailsManagerComponent implements OnInit, OnChanges {
    // #region [ViewChild]
    @ViewChild('backdropRef') backdropRef: ElementRef;
    @ViewChild('detailsAreaRef') detailsAreaRef: ElementRef;
    @ViewChild('flowDefinitionDetailsRef') flowDefinitionDetailsRef: FlowDefinitionDetailsDefaultComponent;
    @ViewChild('flowDefinitionDetailsHotConfigsRef') flowDefinitionDetailsHotConfigsRef: FlowDefinitionDetailsHotConfigsComponent;
    @ViewChild('flowObjectDetailsRef') flowObjectDetailsRef: FlowObjectDetailsDefaultComponent;
    @ViewChild('flowObjectFormRef') flowObjectFormRef: FlowObjectDetailsFormComponent;
    @ViewChild('flowObjectStartInboundApiRef') flowObjectStartInboundApiRef: FlowObjectDetailsStartInboundComponent;
    @ViewChild('modelEditorRef') modelEditorRef: FlowObjectDetailsModelEditorComponent;
    @ViewChild('flowObjectOutboundApiRef') flowObjectOutboundApiRef: FlowObjectDetailsOutboundApiComponent;
    @ViewChild('flowObjectExtraContentRef') flowObjectExtraContentRef: FlowObjectDetailsExtraContentComponent;
    @ViewChild('flowObjectMidwayOutboundApiRef') flowObjectMidwayOutboundApiRef: FlowObjectDetailsMidwayOutboundApiComponent;
    @ViewChild('flowObjectGatewayRulesRef') flowObjectGatewayRulesRef: FlowObjectDetailsGatewayRulesComponent;
    @ViewChild('flowObjectGatewayPathsRef') flowObjectGatewayPathsRef: FlowObjectDetailsGatewayPathsComponent;
    // #endregion

    // #region [listeners]
    @HostListener('window:keydown', ['$event'])
    onKeyDown(event: KeyboardEvent) {
        // tentar evitar comportamento padrão de browser de salvar página com "CTRL + S"
        if (!this.$isReadOnlyMode() && (event.metaKey || event.ctrlKey) && event.key === 's') {
            event.preventDefault();
            event.stopPropagation();
        }

        // atalho para salvar com "CTRL + B", "CTRL + \" ou "CTRL + NUM 0"
        if (
            !this.$isReadOnlyMode()
            && (event.metaKey || event.ctrlKey)
            && (
                event.key === 'b'
                || event.key === 'B'
                || event.key === '\\'
                || event.code === 'Numpad0'
            )
        ) {
            switch (this.$activeDetailsTab()) {
                case Enums.DetailsAreaTabs.FlowDefinitionDetails: this.flowDefinitionDetailsRef?.onSubmit(); break;
                case Enums.DetailsAreaTabs.FlowDefinitionDetailsHotConfigs: this.flowDefinitionDetailsHotConfigsRef?.onSubmit(); break;
                case Enums.DetailsAreaTabs.FlowObjectDetails: this.flowObjectDetailsRef?.onSubmit(); break;
                case Enums.DetailsAreaTabs.FormBuilder: this.flowObjectFormRef?.onSubmit(); break;
                case Enums.DetailsAreaTabs.FormBuilderInboundApi: this.flowObjectStartInboundApiRef?.onSubmit(); break;
                case Enums.DetailsAreaTabs.HtmlEditor: this.modelEditorRef?.onSubmit(); break;
                case Enums.DetailsAreaTabs.OutboundApi: this.flowObjectOutboundApiRef?.onSubmit(); break;
                case Enums.DetailsAreaTabs.MidwayOutboundApi: this.flowObjectMidwayOutboundApiRef?.onSubmit(); break;
                case Enums.DetailsAreaTabs.GatewayRules: this.flowObjectGatewayRulesRef?.onSubmit(); break;
                case Enums.DetailsAreaTabs.GatewayPaths: this.flowObjectGatewayPathsRef?.onSubmit(); break;
                case Enums.DetailsAreaTabs.ExtraContent: this.flowObjectExtraContentRef?.onSubmit(); break;
            }

            this._saveFlowDefinitionEvent.emit();
            event.preventDefault();
            event.stopPropagation();
        }
    }
    // #endregion

    // #region [Type props]
    DetailsAreaTabs = Enums.DetailsAreaTabs;
    DetailsAreaTabsLabel = Enums.DetailsAreaTabsLabel;
    Enums = Enums;
    Object = Object;
    Utils = Utils;
    // #endregion

    // #region [props]
    activeTabIndex: number|undefined = undefined;
    flowTargetOptions: IBaseOption[] = [];
    // #endregion

    // #region [signals]
    $flowDefinition = signal(null as FlowDefinition);
    $flowObjectDefinition = signal(null as FlowObjectDefinition);
    $isAreaVisible = signal(new VisibleArea());
    $isReadOnlyMode = signal(false);
    $isLatestVersion = signal(false);
    $isCreate = signal(false);
    $openModalAreaTrigger = signal(false);
    $toggleEditAreaTrigger = signal(null as FlowObjectDefinition);
    $activeDetailsTab = signal(null as Enums.DetailsAreaTabs);
    $previousActiveDetailsTab = signal(null as Enums.DetailsAreaTabs);
    $ongoingTinyMceRecycle = signal(false);
    $shouldShowModelEditor = signal(true);
    $tinyMceContent = signal(HTML_TEMPLATE_DEFAULT);
    $tinyMceModelEditorOptions = signal(FOD_MODEL_EDITOR_TINYMCE_OPTIONS as any);
    $tinyMceExtraContentOptionsMessage = signal(FOD_EXTRA_CONTENT_MESSAGE_TINYMCE_OPTIONS as any);
    $tinyMceExtraContentOptionsWaitingMessage = signal(FOD_EXTRA_CONTENT_WAITING_MESSAGE_TINYMCE_OPTIONS as any);
    // #endregion

    // #region [computeds]
    $shouldLoadFlowDefinitionDetailsTinyMce = computed(() => this.$activeDetailsTab() == Enums.DetailsAreaTabs.FlowDefinitionDetails);
    $isDetailsAreaExtraSize = computed(() =>
        this.$activeDetailsTab() != null
        && ![
            Enums.DetailsAreaTabs.FlowDefinitionDetails,
            Enums.DetailsAreaTabs.FlowDefinitionDetailsHotConfigs,
            Enums.DetailsAreaTabs.FlowObjectDetails,
            Enums.DetailsAreaTabs.GatewayPaths,
            Enums.DetailsAreaTabs.GatewayRules,
            Enums.DetailsAreaTabs.ExtraContent
        ].includes(this.$activeDetailsTab())
    );
    $isAnyDetailsAreaOpen = computed(() => {
        for (let prop in this.$isAreaVisible()) {
            if (this.$isAreaVisible()[prop] == true) {
                return true;
            }
        }
        return false;
    });
    // #endregion

    // #region [effects]
    $activeDetailsTabEvent = effect(() => this._activeDetailsTabEvent.emit(this.$activeDetailsTab()));
    $isAnyDetailsAreaOpenEvent = effect(() => this._isAnyDetailsAreaOpenEvent.emit(this.$isAnyDetailsAreaOpen()));
    $isDetailsAreaExtraSizeEvent = effect(() => this._isDetailsAreaExtraSizeEvent.emit(this.$isDetailsAreaExtraSize()));
    $openModalAreaTriggerEvent = effect(() => {
        if (this.$openModalAreaTrigger()) {
            this.openModalArea();
            this.$openModalAreaTrigger.set(false);
            this._openModalAreaTriggerResetEvent.emit();
        }
    }, { allowSignalWrites: true });
    $toggleEditAreaTriggerEvent = effect(() => {
        if (this.$toggleEditAreaTrigger() != null) {
            this.toggleEditArea(this.$toggleEditAreaTrigger());
            this.$toggleEditAreaTrigger.set(null);
            this._toggleEditAreaTriggerResetEvent.emit();
        }
    }, { allowSignalWrites: true });
    // #endregion

    // #region [Input/Output]
    @Input() _flowDefinition: FlowDefinition;
    @Input() _isReadOnlyMode: boolean;
    @Input() _isLatestVersion: boolean;
    @Input() _isCreate: boolean;
    @Input() _openModalAreaTrigger: boolean;
    @Input() _toggleEditAreaTrigger: FlowObjectDefinition;
    @Output() _activeDetailsTabEvent = new EventEmitter<Enums.DetailsAreaTabs>();
    @Output() _isAnyDetailsAreaOpenEvent = new EventEmitter<boolean>();
    @Output() _isDetailsAreaExtraSizeEvent = new EventEmitter<boolean>();
    @Output() _tabChangedFormTriggerEvent = new EventEmitter<boolean>();
    @Output() _openModalAreaTriggerResetEvent = new EventEmitter<any>();
    @Output() _toggleEditAreaTriggerResetEvent = new EventEmitter<any>();
    @Output() _saveFlowDefinitionEvent = new EventEmitter<any>();
    @Output() _updateFlowDefinitionEvent = new EventEmitter<FlowDefinition>();
    @Output() _updateFlowDefinitionHotConfigsEvent = new EventEmitter<FlowDefinition>();
    @Output() _updateFlowObjectEvent = new EventEmitter<FlowObjectDefinition>();
    // #endregion

    constructor(
        private spinner: NgxSpinnerService,
        private toastr: ToastrService,
        private dialog: MatDialog, /* utilizado pelo previewPdf via "context"; não remover */
        private flowObjectDefinitionService: FlowObjectDefinitionService, /* utilizado pelo previewPdf via "context"; não remover */
    ) {
        this.flowTargetOptions = Array.from(FlowTargetLabel, item => {
            return {
                value: item[0].toString(),
                description: item[1]
            };
        });
    }

    ngOnInit() {
        this.activeTabIndex = 0;

        Utils.updSignal(this.$tinyMceModelEditorOptions, x => {
            x.previewPdf = () => this.previewPdf(this);
            x.addDynamicMenu = editor => {
                editor.ui.registry.addMenuButton('vForm', {
                    text: 'Formulário',
                    fetch: callback => callback(this.getTinyMceMenuFormEntries(this.$flowObjectDefinition(), editor))
                });
            };
            return x;
        });

        let formFlowObject = this.$flowDefinition()?.flowObjectDefinitions?.find(x => x.typeId == FlowObjectType.StartForm);
        Utils.updSignal(this.$tinyMceExtraContentOptionsMessage, x => {
            x.previewPdf = () => this.previewPdf(this);
            x.addDynamicMenu = editor => {
                editor.ui.registry.addMenuButton('vForm', {
                    text: 'Formulário',
                    fetch: callback => callback(this.getTinyMceMenuFormEntries(formFlowObject, editor))
                });
            };
            return x;
        });

        Utils.updSignal(this.$tinyMceExtraContentOptionsWaitingMessage, x => {
            x.previewPdf = () => this.previewPdf(this);
            x.addDynamicMenu = editor => {
                editor.ui.registry.addMenuButton('vForm', {
                    text: 'Formulário',
                    fetch: callback => callback(this.getTinyMceMenuFormEntries(formFlowObject, editor))
                });
            };
            return x;
        });
    }

    ngOnChanges() {
        this.$flowDefinition.set(this._flowDefinition);
        this.$isReadOnlyMode.set(this._isReadOnlyMode);
        this.$isLatestVersion.set(this._isLatestVersion);
        this.$isCreate.set(this._isCreate);
        this.$openModalAreaTrigger.set(this._openModalAreaTrigger);
        this.$toggleEditAreaTrigger.set(this._toggleEditAreaTrigger);
    }

    openModalArea(flowObject?: FlowObjectDefinition) {
        if (this.detailsAreaRef == null || this.backdropRef == null) {
            setTimeout(() => this.openModalArea(flowObject), 300);
            return;
        }

        [this.detailsAreaRef.nativeElement, this.backdropRef.nativeElement].forEach(elem => {
            elem.style.visibility = 'visible';
            elem.style.opacity = '1';
        });

        if (flowObject != null) {
            // editando FlowObject
            this.$flowObjectDefinition.set(Utils.clone(flowObject));
            Utils.updSignal(this.$isAreaVisible, x => {
                x.flowObjectDetails = true;
                return x;
            });
            this.$previousActiveDetailsTab.set(this.$activeDetailsTab());
            this.$activeDetailsTab.set(Enums.DetailsAreaTabs.FlowObjectDetails);

            if (flowObject.documentHtmlContent != null && flowObject.documentHtmlContent != '') {
                this.$tinyMceContent.set(flowObject.documentHtmlContent);
            } else {
                this.$tinyMceContent.set(HTML_TEMPLATE_DEFAULT);
                flowObject.documentHtmlContent = HTML_TEMPLATE_DEFAULT;
            }
        } else {
            // editando FlowDefinition
            Utils.updSignal(this.$isAreaVisible, x => {
                x.flowDefinitionDetails = true;
                return x;
            });
            Utils.updSignal(this.$isAreaVisible, x => {
                x.flowDefinitionDetailsHotConfigs = true;
                return x;
            });
            this.$previousActiveDetailsTab.set(this.$activeDetailsTab());
            this.$activeDetailsTab.set(Enums.DetailsAreaTabs.FlowDefinitionDetails);
        }
    }

    closeModalAreas() {
        let elements = [this.detailsAreaRef.nativeElement, this.backdropRef.nativeElement];
        elements.forEach(elem => elem.style.opacity = '0');
        setTimeout(() => elements.forEach(elem => elem.style.visibility = 'hidden'), 200);

        this.$isAreaVisible.set(new VisibleArea());
        this.activeTabIndex = undefined;
        this.$previousActiveDetailsTab.set(this.$activeDetailsTab());
        this.$activeDetailsTab.set(undefined);
    }

    toggleEditArea(flowObject?: FlowObjectDefinition) {
        if (flowObject == null) {
            this.closeModalAreas();
            return;
        }

        switch (flowObject.typeId) {
            case FlowObjectType.StartForm:
                Utils.updSignal(this.$isAreaVisible, x => {
                    x.formBuilder = true;
                    return x;
                });
                Utils.updSignal(this.$isAreaVisible, x => {
                    x.htmlEditor = true;
                    return x;
                });
                break;

            case FlowObjectType.StartInboundApi:
                Utils.updSignal(this.$isAreaVisible, x => {
                    x.formBuilderInboundApi = true;
                    return x;
                });
                break;

            case FlowObjectType.FinishOutboundApi:
                Utils.updSignal(this.$isAreaVisible, x => {
                    x.api = true;
                    return x;
                });
                break;

            case FlowObjectType.TaskMidwayOutboundApi:
                Utils.updSignal(this.$isAreaVisible, x => {
                    x.midApi = true;
                    return x;
                });
                break;

            case FlowObjectType.GatewayRules:
                Utils.updSignal(this.$isAreaVisible, x => {
                    x.gatewayRules = true;
                    return x;
                });
                break;

            case FlowObjectType.GatewayPaths:
                Utils.updSignal(this.$isAreaVisible, x => {
                    x.gatewayPaths = true;
                    return x;
                });
                break;

            case FlowObjectType.FinishContent:
                Utils.updSignal(this.$isAreaVisible, x => {
                    x.extraContent = true;
                    return x;
                });
                break;

            case FlowObjectType.GatewayApprove:
                Utils.updSignal(this.$isAreaVisible, x => {
                    x.extraContent = true;
                    return x;
                });
                break;

            case FlowObjectType.TaskAcknowledge:
                Utils.updSignal(this.$isAreaVisible, x => {
                    x.extraContent = true;
                    return x;
                });
                break;
        }

        this.openModalArea(flowObject);
    }

    async tabChanged(event: MatTabChangeEvent) {
        this.$previousActiveDetailsTab.set(this.$activeDetailsTab());
        this.$activeDetailsTab.set(Enums.DetailsAreaTabsLabelReversed.get(event.tab.textLabel));

        if (this.$previousActiveDetailsTab() == Enums.DetailsAreaTabs.HtmlEditor) {
            setTimeout(() => this.$shouldShowModelEditor.set(false), 750);
        }

        if (this.$activeDetailsTab() == Enums.DetailsAreaTabs.HtmlEditor) {
            this.spinner.show('tinyMceSpinner');
            this.$shouldShowModelEditor.set(true);

            if (Utils.isNullOrEmpty(this.$flowObjectDefinition().formSchema)) {
                let formSchema = await this.getFormSchema(this.$flowObjectDefinition().id);
                Utils.updSignal(this.$flowObjectDefinition, x => {
                    x.formSchema = formSchema;
                    return x;
                });
            }
        }

        if (this.$activeDetailsTab() == Enums.DetailsAreaTabs.FormBuilder) {
            if (this.$isCreate()) {
                Utils.updSignal(this.$flowObjectDefinition, x => {
                    x.formSchema = JSON.stringify(FORMBUILDER_SCHEMA_DEFAULT);
                    return x;
                });
            } else {
                if (Utils.isNullOrEmpty(this.$flowObjectDefinition().formSchema)) {
                    let formSchema = await this.getFormSchema(this.$flowObjectDefinition().id);
                    Utils.updSignal(this.$flowObjectDefinition, x => {
                        x.formSchema = formSchema;
                        return x;
                    });
                }
            }
        }

        if (this.$activeDetailsTab() == Enums.DetailsAreaTabs.FormBuilderInboundApi) {
            if (this.$isCreate()) {
                Utils.updSignal(this.$flowObjectDefinition, x => {
                    x.formSchema = JSON.stringify(FORMBUILDER_SCHEMA_DEFAULT);
                    return x;
                });
            } else {
                if (Utils.isNullOrEmpty(this.$flowObjectDefinition().formSchema)) {
                    let formSchema = await this.getFormSchema(this.$flowObjectDefinition().id);
                    Utils.updSignal(this.$flowObjectDefinition, x => {
                        x.formSchema = formSchema;
                        return x;
                    });
                }
            }
        }

        if (this.$activeDetailsTab() == Enums.DetailsAreaTabs.FormBuilder) {
            this.flowObjectFormRef.startProcessingConditionalTags();
        }

        if (this.$activeDetailsTab() == Enums.DetailsAreaTabs.ExtraContent) {
            this.$ongoingTinyMceRecycle.set(true);
        } else {
            this.$ongoingTinyMceRecycle.set(false);
        }
    }

    tinyMceFinishedLoading() {
        this.spinner.hide('tinyMceSpinner');
    }

    onSubmitForm(event) {
        this._updateFlowObjectEvent.emit(event);
        Utils.setSignal(this.$flowObjectDefinition, event);
    }

    // #region [===== private methods =====]
    // #endregion

    private async previewPdf(context: DetailsManagerComponent) {
        context.spinner.show('flow');

        const response = await context.flowObjectDefinitionService.previewPdf({
            documentHtmlContent: context.$tinyMceContent(),
            unitId: context.$flowDefinition().unitId
        });

        context.spinner.hide('flow');

        if (!response.isSuccess) {
            context.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        const previewDialog = context.dialog.open(PdfPreviewDialogComponent, {
            data: response.data
        });

        document.querySelector('.mat-mdc-dialog-panel').classList.add('preview-pdf');

        previewDialog.afterClosed().subscribe(() => {
            document.querySelector('.mat-mdc-dialog-panel')?.classList.remove('preview-pdf');
        });
    }

    private getTinyMceMenuFormEntries(flowObject: FlowObjectDefinition, editor): any[] {
        if (flowObject.formSchema == null || flowObject.formSchema == '') return;

        let entries = [];
        let formSchema = JSON.parse(flowObject.formSchema) as FormSchema;

        formSchema.components.forEach(item => {
            if (['table', 'tableCustom'].includes(item.type)) {
                item.rows.forEach(row => {
                    row.forEach(subitem => {
                        subitem.components.forEach(subsubitem => {
                            entries.push({
                                type: 'menuitem',
                                text: subsubitem.label,
                                onAction: () => editor.insertContent(`{|${subsubitem.key}|}`)
                            });
                        });
                    });
                });
            } else if (!['button'].includes(item.type)) {
                entries.push({
                    type: 'menuitem',
                    text: item.label,
                    onAction: () => editor.insertContent(`{|${item.key}|}`)
                });
            }
        });

        return entries;
    }

    private async getFormSchema(id): Promise<string> {
        const response = await this.flowObjectDefinitionService.getFormSchema(id);

        if (!response.isSuccess) {
            this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return null;
        }

        return response.data;
    }
}
